/**
 * lazyWithRetry — drop-in replacement for React.lazy that auto-recovers
 * from stale-deploy ChunkLoadError.
 *
 * Why this exists:
 *   When a new build is deployed, the user's already-loaded index.html
 *   still references the OLD hashed chunk filenames. As soon as React
 *   tries to render a lazy route, it hits "Loading chunk N failed".
 *   The window 'error' listener in chunkLoadRecovery.js doesn't see this
 *   because React Router v6's <Suspense> + errorElement catches it first.
 *
 * Behaviour:
 *   1st failure  → reload the page once, set a sessionStorage marker so
 *                  we don't enter an infinite loop.
 *   2nd failure  → re-throw — let React Router's errorElement show the
 *                  error UI. At this point reloading won't help (network
 *                  is genuinely broken, not stale cache).
 *
 * Marker key is per-chunk so unrelated lazy modules don't share state.
 */
import { lazy } from 'react';

function isChunkLoadFailure(err) {
  if (!err) return false;
  const haystack = `${err.name || ''} ${err.message || ''} ${String(err)}`;
  return (
    /ChunkLoadError/i.test(haystack) ||
    /Loading chunk [\w-]+ failed/i.test(haystack) ||
    /Failed to fetch dynamically imported module/i.test(haystack) ||
    /error loading dynamically imported module/i.test(haystack) ||
    /Importing a module script failed/i.test(haystack)
  );
}

export function lazyWithRetry(factory, label = 'chunk') {
  return lazy(() => {
    const markerKey = `pinksisly:chunk-retry:${label}`;
    return factory().catch((err) => {
      if (!isChunkLoadFailure(err)) {
        // Not a chunk-load problem — bubble it up untouched.
        throw err;
      }
      let alreadyRetried = false;
      try {
        alreadyRetried = sessionStorage.getItem(markerKey) === '1';
      } catch {
        /* sessionStorage blocked (Safari private, etc.) — proceed without dedup */
      }
      if (alreadyRetried) {
        console.error(
          `[lazyWithRetry] chunk "${label}" failed again after reload; surfacing error.`
        );
        throw err;
      }
      try {
        sessionStorage.setItem(markerKey, '1');
      } catch {
        /* ignore */
      }
      console.warn(`[lazyWithRetry] chunk "${label}" missing — reloading to fetch fresh assets.`);
      // Tiny delay so React has time to commit; reload replaces the page anyway.
      window.location.reload();
      // Return a never-resolving promise so React doesn't try to render
      // anything in the brief window before the reload kicks in.
      return new Promise(() => {});
    });
  });
}

/** Clear all retry markers — call on app boot once the fresh bundle is up. */
export function clearChunkRetryMarkers() {
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('pinksisly:chunk-retry:')) {
        sessionStorage.removeItem(key);
      }
    }
  } catch {
    /* ignore */
  }
}
